import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { getUsers, createUser, updateUser, deleteUser, getUserProjects, assignUserProjects, getProjects } from '../services/api';
import './UserManagement.css';

function UserManagement({ isOpen, onClose }) {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // null = create mode, user object = edit mode
  const [showProjectAssignment, setShowProjectAssignment] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '', role: 'editor' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAdmin() && isOpen) {
      fetchUsers();
      fetchProjects();
    }
  }, [isAdmin, isOpen]);

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Find the original admin (lowest ID with role='admin')
  const getOriginalAdminId = () => {
    const adminUsers = users.filter(u => u.role === 'admin');
    if (adminUsers.length === 0) return null;
    return Math.min(...adminUsers.map(u => u.id));
  };

  const isOriginalAdmin = (userId) => {
    const originalAdminId = getOriginalAdminId();
    return originalAdminId !== null && userId === originalAdminId;
  };

  const fetchProjects = async () => {
    try {
      const data = await getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const openCreateUser = () => {
    setEditingUser(null);
    setFormData({ username: '', password: '', role: 'editor' });
    setError(null);
    setShowUserForm(true);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setFormData({ username: user.username, password: '', role: user.role });
    setError(null);
    setShowUserForm(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await createUser(formData);
      setShowUserForm(false);
      setEditingUser(null);
      setFormData({ username: '', password: '', role: 'editor' });
      fetchUsers();
      toast.success('User created successfully');
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to create user';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setLoading(true);
    setError(null);

    const payload = {
      username: formData.username,
      role: formData.role
    };
    if (formData.password && formData.password.trim()) {
      payload.password = formData.password;
    }

    try {
      await updateUser(editingUser.id, payload);
      setShowUserForm(false);
      setEditingUser(null);
      setFormData({ username: '', password: '', role: 'editor' });
      fetchUsers();
      toast.success('User updated successfully');
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to update user';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await deleteUser(userId);
      fetchUsers();
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error('Failed to delete user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAssignProjects = async (projectIds) => {
    setLoading(true);
    try {
      await assignUserProjects(selectedUser.id, projectIds);
      setShowProjectAssignment(false);
      setSelectedUser(null);
      toast.success(`Projects assigned successfully to ${selectedUser.username}`);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to assign projects';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin() || !isOpen) {
    return null;
  }

  return (
    <div className="user-management-overlay" onClick={onClose}>
      <div className="user-management-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-management-header">
          <h2>User Management</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="user-management-content">
          <div className="user-management-actions">
            <button className="btn-primary" onClick={openCreateUser}>
              + Add User
            </button>
          </div>

      {showUserForm && (
        <UserForm
          mode={editingUser ? 'edit' : 'create'}
          formData={formData}
          setFormData={setFormData}
          onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
          lockRole={editingUser && isOriginalAdmin(editingUser.id)}
          onCancel={() => {
            setShowUserForm(false);
            setEditingUser(null);
            setFormData({ username: '', password: '', role: 'editor' });
            setError(null);
          }}
          loading={loading}
          error={error}
        />
      )}

      {showProjectAssignment && selectedUser && (
        <ProjectAssignment
          user={selectedUser}
          projects={projects}
          onAssign={handleAssignProjects}
          onCancel={() => {
            setShowProjectAssignment(false);
            setSelectedUser(null);
          }}
          loading={loading}
        />
      )}

          <div className="users-list">
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td><span className={`role-badge role-${user.role}`}>{user.role}</span></td>
                    <td>
                      {(user.role === 'editor' || user.role === 'viewer' || user.role === 'supervisor') && (
                        <button
                          type="button"
                          className="btn-small"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowProjectAssignment(true);
                          }}
                        >
                          Assign Projects
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-small btn-edit"
                        onClick={() => openEditUser(user)}
                      >
                        Edit
                      </button>
                      {!isOriginalAdmin(user.id) && (
                        <button
                          type="button"
                          className="btn-small btn-danger"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserForm({ mode, formData, setFormData, onSubmit, onCancel, loading, error, lockRole }) {
  const isEdit = mode === 'edit';

  return (
    <div className="user-form-overlay" onClick={onCancel}>
      <div className="user-form-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{isEdit ? 'Edit User' : 'Create New User'}</h3>
        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label>Password {isEdit && <span className="field-hint">(optional — leave blank to keep current)</span>}</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!isEdit}
              autoComplete="new-password"
              placeholder={isEdit ? 'Leave blank to keep current password' : ''}
            />
          </div>
          <div className="form-group">
            <label>Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              disabled={lockRole}
              title={lockRole ? 'The primary admin role cannot be changed' : ''}
            >
              <option value="admin">Admin</option>
              <option value="supervisor">Supervisor</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
            {lockRole && (
              <p className="field-hint-block">The original administrator must stay an admin.</p>
            )}
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="form-actions">
            <button type="button" onClick={onCancel}>Cancel</button>
            <button type="submit" disabled={loading}>
              {loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save changes' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProjectAssignment({ user, projects, onAssign, onCancel, loading }) {
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [userProjects, setUserProjects] = useState([]);

  useEffect(() => {
    fetchUserProjects();
  }, [user.id]);

  const fetchUserProjects = async () => {
    try {
      const response = await getUserProjects(user.id);
      setSelectedProjects(response.projectIds || []);
      setUserProjects(response.projectIds || []);
    } catch (error) {
      console.error('Error fetching user projects:', error);
    }
  };

  const handleToggleProject = (projectId) => {
    if (selectedProjects.includes(projectId)) {
      setSelectedProjects(selectedProjects.filter(id => id !== projectId));
    } else {
      setSelectedProjects([...selectedProjects, projectId]);
    }
  };

  const handleSave = () => {
    onAssign(selectedProjects);
  };

  return (
    <div className="project-assignment-overlay" onClick={onCancel}>
      <div className="project-assignment-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Assign Projects to {user.username}</h3>
        <div className="projects-checklist">
          {projects.map(project => (
            <label key={project.id} className="checkbox-label">
              <input
                type="checkbox"
                checked={selectedProjects.includes(project.id)}
                onChange={() => handleToggleProject(project.id)}
              />
              <span>{project.name}</span>
            </label>
          ))}
        </div>
        <div className="form-actions">
          <button type="button" onClick={onCancel}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserManagement;

